import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  VStack,
  Text,
  IconButton,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Button,
  Textarea,
  HStack,
} from "@chakra-ui/react";
import { FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useOrderChat, UseOrderChatInput } from "../hooks/useOrderChat";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { useUnreadStore } from "../store/useOrderStore";
import FileInput, { FileInputRef } from "./FileInput";
import AttachmentPreview from "./AttachmentPreview";
import MediaDisplay from "./MediaDisplay";
import ImagePreviewModal from "./ImagePreviewModal";
import { ChatBoxConfig } from "../../../types/chatConfigs";
import { CHAT_THEME } from "../../../constants/chatTheme";
import { isImage, downloadMediaFile } from "../utils/fileUtils";

interface ChatBoxProps {
  onClose: () => void;
  config: ChatBoxConfig;
  orderNumber: string;
  participantName: string;
  orderHandlerUserId: string;
  participantId: string;
}

const MotionBox = motion(Box);

const ChatBox: React.FC<ChatBoxProps> = ({
  onClose,
  config,
  orderNumber,
  participantName,
  orderHandlerUserId,
  participantId,
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<FileInputRef>(null);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<{
    media: any;
    blob: Blob;
  } | null>(null);

  const userDetails = useUserDetailsContext();
  const isCurrentUserOrderHandler = userDetails?._id === orderHandlerUserId;

  const chatOptions: UseOrderChatInput = {
    chatType: config.chatType,
    orderNo: orderNumber,
    orderHandlerUserId,
    participantId,
    participantName,
  };

  const {
    ready,
    error,
    messages,
    sendMessage,
    conversation,
    isLoading,
    initializeChat,
  } = useOrderChat(chatOptions);

  const { clearUnreadCountByUniqueName } = useUnreadStore();

  const uniqueName = `order:${orderNumber}|${config.chatType}:${participantId}`;

  const chatStyles = useMemo(
    () => ({
      ...config.styles,
      ...config.content,
    }),
    [config.styles, config.content]
  );

  const counts = useUnreadStore((s) => s.counts);
  const unreadCountForThis = useMemo(() => {
    return counts.get(uniqueName) ?? 0;
  }, [counts, uniqueName]);

  const themeColors = useMemo(
    () => ({
      headerBg: unreadCountForThis > 0 ? "green.100" : "blue.50",
      accentColor: unreadCountForThis > 0 ? "green.600" : "blue.600",
      borderColor: unreadCountForThis > 0 ? "green.200" : "blue.200",
      inputBg: unreadCountForThis > 0 ? "green.50" : "blue.50",
      focusBorder: unreadCountForThis > 0 ? "green.300" : "blue.300",
      focusShadow:
        unreadCountForThis > 0
          ? "0 0 0 1px var(--chakra-colors-green-300)"
          : "0 0 0 1px var(--chakra-colors-blue-300)",
    }),
    [unreadCountForThis]
  );
  const [shakeSend, setShakeSend] = useState(false);
  const prevCountRef = useRef<number>(unreadCountForThis);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = unreadCountForThis;

    if (unreadCountForThis > prev) {
      setShakeSend(true);
      const timer = setTimeout(() => setShakeSend(false), 600);
      return () => clearTimeout(timer);
    }
  }, [unreadCountForThis]);

  const getChatBoxPosition = () => config.positioning.getPosition();

  useEffect(() => {
    if (isCurrentUserOrderHandler && !ready && !isLoading) {
      initializeChat();
    }
  }, [
    isCurrentUserOrderHandler,
    orderNumber,
    ready,
    isLoading,
    initializeChat,
  ]);

  useEffect(() => {
    if (isCurrentUserOrderHandler && ready && conversation) {
      clearUnreadCountByUniqueName(uniqueName);
      conversation.setAllMessagesRead().catch((err) => {
        console.error("Failed to mark messages as read:", err);
      });
      if (messages.length > 0) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (inputRef.current && ready && !isLoading) {
      inputRef.current.focus();
    }
  }, [
    isCurrentUserOrderHandler,
    orderNumber,
    conversation,
    ready,
    messages,
    clearUnreadCountByUniqueName,
    uniqueName,
  ]);

  const handleSendMessage = async () => {
    if (
      (!messageText.trim() && selectedFiles.length === 0) ||
      isSending ||
      !ready
    ) {
      return;
    }

    setIsSending(true);
    try {
      await sendMessage(messageText, selectedFiles);
      setMessageText("");
      setSelectedFiles([]);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    const newFiles: File[] = [];
    let hasImages = false;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (isImage(item.type)) {
        hasImages = true;
        const file = item.getAsFile();
        if (file) {
          const timestamp = Date.now();
          const extension = file.type.split("/")[1] ?? "";
          const fileName = `image-${timestamp}.${extension}`;
          const newFile = new File([file], fileName, { type: file.type });
          newFiles.push(newFile);
        }
      }
    }

    if (hasImages) {
      e.preventDefault();
    }

    if (newFiles.length > 0) {
      const totalFiles = selectedFiles.length + newFiles.length;
      if (totalFiles > CHAT_THEME.LIMITS.MAX_FILES) {
        fileInputRef.current?.setError(
          `Cannot attach more than ${CHAT_THEME.LIMITS.MAX_FILES} files`
        );
        return;
      }

      const currentTotalSize = selectedFiles.reduce(
        (sum, file) => sum + file.size,
        0
      );
      const newTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
      const totalSize = currentTotalSize + newTotalSize;

      if (totalSize > CHAT_THEME.LIMITS.MAX_FILE_SIZE_BYTES) {
        fileInputRef.current?.setError(
          `Total file size cannot exceed ${CHAT_THEME.LIMITS.MAX_FILE_SIZE_MB}MB`
        );
        return;
      }

      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const formatMessageDate = (date: Date | null) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const formatMessageTime = (date: Date | null) => {
    if (!date) return "";
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const groupMessagesByDate = (messages: any[]) => {
    const groups: { [key: string]: any[] } = {};

    messages.forEach((message) => {
      const date = message.dateCreated;
      if (date) {
        const dateKey = formatMessageDate(date);
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(message);
      }
    });

    return groups;
  };

  const isCurrentUserMessage = (message: any) => {
    return message.author?.startsWith("orca:");
  };

  if (!isCurrentUserOrderHandler) {
    return (
      <AnimatePresence>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          position="fixed"
          bottom="20px"
          {...(config.positioning.getSide() === "left"
            ? { left: getChatBoxPosition() }
            : { right: getChatBoxPosition() })}
          bg="white"
          borderRadius="xl"
          boxShadow="2xl"
          zIndex={1000}
          width="250px"
          border="1px"
          borderColor="gray.200"
          ref={chatBoxRef}
        >
          <Box p={4} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              You are not authorized to chat for this order.
            </Text>
            <Button
              mt={3}
              onClick={onClose}
              colorScheme="blue"
              size="sm"
              borderRadius="lg"
            >
              Close
            </Button>
          </Box>
        </MotionBox>
      </AnimatePresence>
    );
  }

  return (
    <>
      <AnimatePresence>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          position="fixed"
          bottom="1px"
          {...(config.positioning.getSide() === "left"
            ? { left: getChatBoxPosition() }
            : { right: getChatBoxPosition() })}
          bg="white"
          borderRadius="xl"
          boxShadow="2xl"
          zIndex={1000}
          width={CHAT_THEME.SIZES.CHAT_WIDTH}
          height={CHAT_THEME.SIZES.CHAT_HEIGHT}
          border="1px"
          borderColor="gray.200"
          display="flex"
          flexDirection="column"
          ref={chatBoxRef}
        >
          {/* Header */}
          <Box
            p={3}
            borderBottom="1px"
            borderColor={chatStyles.headerBorder}
            bg={themeColors.headerBg}
            borderTopRadius="xl"
          >
            <Flex justify="space-between" align="center">
              <VStack align="flex-start" spacing={1}>
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color={themeColors.accentColor}>
                    {chatStyles.chatTypeIcon}
                  </Text>
                  <Text fontWeight="bold" fontSize="lg" color="gray.800">
                    Order #{orderNumber}
                  </Text>
                </HStack>
                <HStack spacing={2} align="center">
                  <Text
                    fontSize="xs"
                    color={themeColors.accentColor}
                    fontWeight="medium"
                  >
                    {chatStyles.chatTypeLabel}:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {participantName}
                  </Text>
                </HStack>
              </VStack>
              <HStack spacing={3} align="center">
                {unreadCountForThis > 0 && (
                  <motion.div
                    animate={shakeSend ? { x: [0, -3, 3, -3, 3, 0] } : {}}
                    transition={{ duration: 0.6 }}
                  >
                    <Box
                      bg="red.400"
                      color="white"
                      borderRadius="full"
                      minW="20px"
                      h="20px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="xs"
                      fontWeight="bold"
                      boxShadow="sm"
                      border="2px solid white"
                      role="status"
                      aria-label={`${unreadCountForThis} unread messages`}
                    >
                      {unreadCountForThis > 99 ? "99+" : unreadCountForThis}
                    </Box>
                  </motion.div>
                )}
                <IconButton
                  aria-label="Close chat"
                  icon={<FaTimes />}
                  size="sm"
                  variant="ghost"
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                  onClick={onClose}
                />
              </HStack>
            </Flex>
          </Box>

          {/* Connection Status */}
          {error && (
            <Alert status="error" borderRadius="0" py={2}>
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="xs">Connection Error</AlertTitle>
                <AlertDescription fontSize="xs">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Messages Area */}
          <VStack flex={1} overflowY="auto" spacing={3} p={4} align="stretch">
            {isLoading ? (
              <Flex
                justify="center"
                align="center"
                height="100%"
                direction="column"
              >
                <VStack spacing={3}>
                  <Box position="relative">
                    <Box
                      as={motion.div}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: "1.5s",
                        repeat: "Infinity",
                        ease: "easeInOut",
                      }}
                    >
                      <Spinner size="lg" color="blue.500" thickness="3px" />
                    </Box>
                  </Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    Loading Conversation
                  </Text>
                </VStack>
              </Flex>
            ) : messages.length === 0 ? (
              <Text textAlign="center" color="gray.500" py={6} fontSize="sm">
                No messages yet. Start the conversation!
              </Text>
            ) : (
              Object.entries(groupMessagesByDate(messages)).map(
                ([dateKey, messagesForDate]) => (
                  <VStack key={dateKey} align="stretch" spacing={2}>
                    <Box
                      bg="gray.200"
                      px={2}
                      py={1}
                      borderRadius="md"
                      alignSelf="center"
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.700"
                    >
                      {dateKey}
                    </Box>
                    {messagesForDate.map((message) => (
                      <VStack
                        key={message.sid}
                        alignSelf={
                          isCurrentUserMessage(message)
                            ? "flex-end"
                            : "flex-start"
                        }
                        maxW="80%"
                        align={
                          isCurrentUserMessage(message)
                            ? "flex-end"
                            : "flex-start"
                        }
                        spacing={1}
                      >
                        <Box
                          mb={1}
                          textAlign={
                            isCurrentUserMessage(message) ? "right" : "left"
                          }
                        >
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            fontWeight="medium"
                          >
                            {message.attributes?.authorName || ""}
                          </Text>
                        </Box>

                        <Box
                          bg={
                            isCurrentUserMessage(message)
                              ? chatStyles.messageBg
                              : "gray.50"
                          }
                          color="black"
                          px={3}
                          py={2}
                          borderRadius="lg"
                          position="relative"
                          display="inline-block"
                          maxW="100%"
                          border={
                            isCurrentUserMessage(message)
                              ? `1px solid var(--chakra-colors-${chatStyles.messageBorder})`
                              : "1px solid var(--chakra-colors-gray-200)"
                          }
                          alignSelf={
                            isCurrentUserMessage(message)
                              ? "flex-end"
                              : "flex-start"
                          }
                        >
                          {message.body && (
                            <Text fontSize="sm" whiteSpace="pre-wrap">
                              {message.body}
                            </Text>
                          )}

                          {message.attachedMedia &&
                            message.attachedMedia.length > 0 && (
                              <MediaDisplay
                                media={message.attachedMedia}
                                onImageClick={(media, blob) => {
                                  setImagePreview({ media, blob });
                                }}
                              />
                            )}
                        </Box>

                        <Box
                          mt={1}
                          textAlign={
                            isCurrentUserMessage(message) ? "right" : "left"
                          }
                        >
                          <Text fontSize="xs" color="gray.500">
                            {formatMessageTime(message.dateCreated)}
                          </Text>
                        </Box>
                      </VStack>
                    ))}
                  </VStack>
                )
              )
            )}
            <div ref={messagesEndRef} />
          </VStack>

          {/* Input Area */}
          <Box
            p={2}
            borderTop="1px"
            borderColor={themeColors.borderColor}
            bg={themeColors.inputBg}
          >
            <VStack spacing={2}>
              {/* Attachment Preview */}
              <AttachmentPreview
                files={selectedFiles}
                onRemoveFile={(index) => {
                  const newFiles = selectedFiles.filter((_, i) => i !== index);
                  setSelectedFiles(newFiles);
                }}
              />

              <Textarea
                ref={inputRef}
                placeholder="Write a message..."
                size="md"
                borderRadius="lg"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                isDisabled={!ready || isSending || isLoading}
                _disabled={{ opacity: 0.6 }}
                bg="white"
                border="1px"
                borderColor="gray.300"
                focusBorderColor={themeColors.focusBorder}
                rows={4}
                resize="none"
                overflowY="auto"
                minH="120px"
                maxH="200px"
              />

              <Flex justify="space-between" width="100%" align="center">
                {/* Attachment Buttons */}
                <FileInput
                  ref={fileInputRef}
                  onFilesSelected={setSelectedFiles}
                  selectedFiles={selectedFiles}
                  maxFiles={CHAT_THEME.LIMITS.MAX_FILES}
                  maxTotalSize={CHAT_THEME.LIMITS.MAX_FILE_SIZE_BYTES}
                />

                {/* Send Button */}
                <IconButton
                  aria-label="Send message"
                  icon={
                    isSending ? (
                      <Spinner size="sm" />
                    ) : (
                      <Text fontSize="sm">Send</Text>
                    )
                  }
                  size="md"
                  bg="blue.400"
                  _hover={{ bg: "blue.500", transform: "scale(1.02)" }}
                  _active={{ transform: "scale(0.98)" }}
                  borderRadius="lg"
                  onClick={handleSendMessage}
                  isLoading={isSending}
                  isDisabled={
                    !ready ||
                    (!messageText.trim() && selectedFiles.length === 0) ||
                    isLoading
                  }
                  px={4}
                />
              </Flex>
            </VStack>
          </Box>
        </MotionBox>
      </AnimatePresence>

      <ImagePreviewModal
        isOpen={!!imagePreview}
        media={imagePreview?.media || null}
        imageBlob={imagePreview?.blob || null}
        onClose={() => setImagePreview(null)}
        onDownload={downloadMediaFile}
      />
    </>
  );
};

export default ChatBox;
